import { Plugin, PluginKey } from 'hubspot-prosemirror/pm/state';
import { Decoration, DecorationSet } from 'hubspot-prosemirror/pm/view';
import ReactDOMServer from 'react-dom/server';
import debounce from 'transmute/debounce';
import Raven from 'raven-js';
import { Image } from 'foundations-components/transitional/components/Image';
import styled from 'styled-components';
import { GenericNotFound } from '../../components/GenericNotFound';
import { insertSlice, insertContent, hasAlignmentInHtml, isAtParagraphStart } from '../../utils/documentUtils';
import uuid from '../../utils/uuid';
import { getMentionNodeType, getMentionTrigger } from './suggestionsUtil';
import { INSERT_EMOJI } from 'rich-text-lib/constants/usageTracking';
import { PM_EMOJI } from '../../constants/events';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { Tracker } from '../../../utils/usageTracker';
import { AtMentionNodeView } from './AtMentionNodeView';
import { DefaultAtMentionComponent } from './DefaultAtMentionComponent';
import { View } from 'foundations-components/components/View';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';

// Styled components to replace CSS classes
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSuggestionsList = styled.div.withConfig({
  displayName: "MentionsPlugin__StyledSuggestionsList"
})(["width:100%;background-color:var(--trellis-color-fill-surface-default);border-radius:4px;[data-suggestion-item='true']{padding:8px;cursor:pointer;&::before{border-top:1px solid;}&[data-suggestion-disabled='true']{color:var(--trellis-color-text-core-disabled);cursor:not-allowed;}}[data-suggestion-active='true']{background-color:var(--trellis-color-fill-field-hover);}"]);
export function getRegexp(trigger, allowSpace, canSearch) {
  if (!canSearch) {
    return new RegExp(`(^|\\s)${trigger}$`);
  }
  return allowSpace ? new RegExp(`(^|\\s)${trigger}([\\S]+\\s?[\\S]*)$`) : new RegExp(`(^|\\s)${trigger}([\\S]+)$`);
}

/**
 * @param {ResolvedPos} $position https://prosemirror.net/docs/ref/#model.Resolved_Positions
 * @param {JSONObject} opts
 * @returns {JSONObject | undefined} undefined if no match
 */
export function getMatch($position, opts) {
  if ($position.depth === 0) {
    return undefined;
  }

  // take current para text content upto cursor start.
  // this makes the regex simpler and parsing the matches easier.
  const parastart = $position.before();
  const text = $position.doc.textBetween(parastart, $position.pos, '\n', '\0');
  const regex = getRegexp(opts.trigger, opts.allowSpace, opts.canSearch);
  const match = text.match(regex);

  // if match found, return match with useful information.
  if (match && match.index !== undefined) {
    // adjust match.index to remove the matched extra space
    match.index = match[0].startsWith(' ') ? match.index + 1 : match.index;
    match[0] = match[0].startsWith(' ') ? match[0].substring(1, match[0].length) : match[0];

    // The absolute position of the match in the document
    const from = $position.start() + match.index;
    const to = from + match[0].length;
    const queryText = match[2];
    return {
      range: {
        from,
        to
      },
      queryText
    };
  }
  return undefined;
}
const getNewState = function getNewState() {
  return {
    active: false,
    range: {
      from: 0,
      to: 0
    },
    text: '',
    suggestions: [],
    index: 0 // current active suggestion index
  };
};
export function GenericSuggestionsComponent(props) {
  const {
    suggestions,
    NotFoundComponent,
    text
  } = props;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledSuggestionsList, {
      "data-suggestion-item-list": "true",
      children: !suggestions || !suggestions.length ? NotFoundComponent ? /*#__PURE__*/_jsx(View, {
        padding: "500",
        as: "div",
        children: /*#__PURE__*/_jsx(NotFoundComponent, {
          text: text
        })
      }) : null : suggestions.map((item, key) =>
      /*#__PURE__*/
      // tabIndex makes this element focusable, allowing it to be the relatedTarget of a blur event
      _jsxs(View, {
        padding: "200",
        cursor: "pointer",
        tabIndex: 0,
        "data-suggestion-item": "true",
        "data-suggestion-disabled": item.disabled,
        children: [item.imageUrl ? /*#__PURE__*/_jsx(Image, {
          src: item.imageUrl,
          width: 24,
          height: 24,
          marginInlineEnd: "200",
          display: "inline-block",
          shape: "circle"
        }) : null, item.searchText || item.name]
      }, key))
    })
  );
}
export function getMentionsPlugin(pluginOptions) {
  // TODO: Add empty data error state - https://git.hubteam.com/HubSpot/customer-data-rte/pull/213#discussion_r1593499
  const opts = Object.assign({
    allowSpace: true,
    maxNoOfSuggestions: 20,
    delay: 250,
    getSuggestionsComponent: GenericSuggestionsComponent,
    NotFoundComponent: GenericNotFound,
    canSearch: true
  }, pluginOptions, {
    trigger: getMentionTrigger(pluginOptions.type)
  });

  // Track whether the popover was explicitly dismissed (blur/escape).
  // Prevents debounced search callback from re-showing the popover after dismissal.
  // Reset on focus or when user types new text or starts a new trigger at a different position.
  let dismissed = false;
  // Tracks previous search text value. When text changes, reset dismissed
  // flag to allow popover to reappear (user is continuing their search).
  let lastText = '';
  // Tracks previous trigger position. When position changes, reset dismissed
  // flag to allow popover at new location (e.g., new line/paragraph).
  let lastRange = {
    from: 0,
    to: 0
  };

  // dropdown element
  const el = document.createElement('div');
  el.setAttribute('data-prosemirror-mention-dropdown', pluginOptions.type);
  const getItemAtIndex = function getItemAtIndex(index) {
    const itemListParent = el.querySelector('[data-suggestion-item-list="true"]');
    const itemList = itemListParent ? itemListParent.childNodes : [];
    return itemList[index];
  };
  const addActiveStateAtIndex = function addActiveStateAtIndex(index) {
    const item = getItemAtIndex(index);
    if (item) {
      item.setAttribute('data-suggestion-active', 'true');
    }
  };
  const removeActiveStateAtIndex = function removeActiveStateAtIndex(index) {
    const item = getItemAtIndex(index);
    if (item) {
      item.removeAttribute('data-suggestion-active');
    }
  };
  const setIndex = function setIndex(index, state) {
    removeActiveStateAtIndex(state.index);
    state.index = index;
    addActiveStateAtIndex(state.index);
  };
  const select = function select(view, state) {
    const item = state.suggestions[state.index];
    if (!item || item.disabled) return;
    if (opts.fetchSelectSuggestion) {
      opts.fetchSelectSuggestion({
        snippet: item,
        handleInsert: ({
          html
        }) => {
          let nextState;
          const hasAlignment = hasAlignmentInHtml(html);
          const $from = view.state.doc.resolve(state.range.from);
          const atParagraphStart = isAtParagraphStart($from);
          if (hasAlignment && atParagraphStart) {
            nextState = insertContent({
              htmlBody: html,
              editorState: view.state,
              from: state.range.from,
              to: state.range.to
            });
          } else {
            nextState = insertSlice({
              editorState: view.state,
              htmlBody: html,
              $from,
              $to: view.state.doc.resolve(state.range.to)
            });
          }
          nextState = pluginOptions.usePlaintextMode ? convertToPlaintextState(nextState, pluginOptions.plaintextOptions) : nextState;

          /**
           * updateState and dispatch cannot be called with conflicting states,
           * so they should both be called with the same newState.
           */
          view.updateState(nextState);
          // dispatch to register changed state
          view.dispatch(nextState.tr);
        }
      });
      return;
    }
    let tr;
    if (opts.type === 'EMOJI') {
      Tracker.track(PM_EMOJI, {
        action: INSERT_EMOJI,
        method: 'shortcut',
        value: item.id
      });
      tr = view.state.tr.insertText(item.text, state.range.from, state.range.to);
    } else {
      const attrs = {
        // we want to insert the actual text of the item, not the search text
        name: item.text || item.searchText || item.name,
        id: item.id
      };
      let marks;
      const cursor = view.state.selection.$cursor;
      if (cursor) {
        marks = cursor.marks();
      }
      let node = getMentionNodeType(view.state.schema, opts.type).create(attrs);
      if (marks) {
        node = node.mark(marks);
      }
      tr = view.state.tr.replaceWith(state.range.from, state.range.to, node);
    }
    view.dispatch(tr);
  };
  const safeNodeAtPos = (view, pos) => {
    try {
      const ret = view.domAtPos(pos);
      if (!ret.node.querySelector) {
        return ret.node.parentNode;
      }
      return ret.node;
    } catch (e) {
      // possibly future solution, make this recursive to find a different pos if it's out of bounds?
      // return safeDomAtPos(view, pos - 1);
      return undefined;
    }
  };
  const showList = function showList(view, state, suggestions) {
    var _pluginOptions$leftOf, _window$visualViewpor, _window$visualViewpor2;
    // Don't show if explicitly dismissed (blur/escape) - prevents debounced
    // callback from re-showing popover after user intentionally closed it
    if (dismissed) {
      return;
    }
    // eslint-disable-next-line no-restricted-syntax
    el.innerHTML = ReactDOMServer.renderToString(opts.getSuggestionsComponent({
      suggestions,
      text: state.text,
      NotFoundComponent: opts.NotFoundComponent
    }));
    if (suggestions && suggestions.length) {
      el.querySelectorAll('[data-suggestion-item="true"]').forEach((itemNode, index) => {
        itemNode.addEventListener('click', () => {
          select(view, state);
          view.focus();
        });
        itemNode.addEventListener('mouseover', () => {
          setIndex(index, state);
        });
        itemNode.addEventListener('mouseout', () => {
          setIndex(index, state);
        });
      });

      // highlight first element by default - like Facebook.
      addActiveStateAtIndex(state.index);
    }

    // get current @mention span left and top.
    const node = safeNodeAtPos(view, view.state.selection.$from.pos);
    if (!node) return;
    const textDOM = node.querySelector('[data-suggestion-text="true"]');
    const offset = textDOM && textDOM.getBoundingClientRect();
    if (!offset) {
      el.style.display = 'none';
      return;
    }
    document.body.appendChild(el);
    const MAX_HEIGHT = 200;
    el.style.position = 'fixed';
    if (pluginOptions.width) {
      el.style.width = `${pluginOptions.width}px`;
    } else {
      el.style.maxWidth = '400px';
    }
    el.style.maxHeight = `${MAX_HEIGHT}px`;
    el.style.overflowY = 'auto';
    el.style.zIndex = '9999';
    el.style.border = `1px solid var(--trellis-color-border-core-default)`;
    el.style.boxShadow = '0 3px 8px rgba(0, 0, 0, .1)';
    el.style.left = `${(_pluginOptions$leftOf = pluginOptions.leftOffset) !== null && _pluginOptions$leftOf !== void 0 ? _pluginOptions$leftOf : offset.left}px`;
    const wasHidden = el.style.display === 'none' || !el.style.display;
    el.style.display = 'block';
    const {
      offsetHeight: textDOMHeight
    } = textDOM;

    // On mobile devices (e.g. iPad), when the on-screen keyboard appears, the visual viewport
    // shifts but getBoundingClientRect() returns values relative to the layout viewport.
    // Adding this offset corrects the dropdown position when the keyboard is showing.
    const viewportOffsetTop = (_window$visualViewpor = (_window$visualViewpor2 = window.visualViewport) === null || _window$visualViewpor2 === void 0 ? void 0 : _window$visualViewpor2.offsetTop) !== null && _window$visualViewpor !== void 0 ? _window$visualViewpor : 0;
    let top;
    if (opts.suggestionsPlacement === 'top') {
      // Place above trigger text
      top = offset.top - el.offsetHeight - 10 + viewportOffsetTop;
    } else if (opts.suggestionsPlacement === 'bottom') {
      // Place below trigger text
      top = textDOMHeight + offset.top + 10 + viewportOffsetTop;
    } else {
      // Smart placement: show below by default, above if near bottom edge
      const onBottomEdge = window.innerHeight - offset.bottom < MAX_HEIGHT + 50;
      top = onBottomEdge ? offset.top - el.offsetHeight - 10 + viewportOffsetTop : textDOMHeight + offset.top + 10 + viewportOffsetTop;
    }
    el.style.top = `${top}px`;
    if (wasHidden) {
      callIfPossible(pluginOptions.onOpenPopover, {
        type: 'inline'
      });
    }
  };
  const hideList = function hideList() {
    dismissed = true;
    const wasVisible = el.style.display !== 'none' && el.style.display;
    if (wasVisible) {
      callIfPossible(pluginOptions.onClosePopover, {
        type: 'inline'
      });
    }
    el.style.display = 'none';
  };
  const scrollActiveItemIntoView = function scrollActiveItemIntoView(index) {
    const activeItem = getItemAtIndex(index);
    if (!activeItem) return;
    const containerRect = el.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    if (itemRect.top < containerRect.top) {
      el.scrollTop -= containerRect.top - itemRect.top;
    } else if (itemRect.bottom > containerRect.bottom) {
      el.scrollTop += itemRect.bottom - containerRect.bottom;
    }
  };
  const goNext = function goNext(_view, state) {
    removeActiveStateAtIndex(state.index);
    state.index++;
    state.index = state.index === state.suggestions.length ? 0 : state.index;
    addActiveStateAtIndex(state.index);
    scrollActiveItemIntoView(state.index);
  };
  const goPrev = function goPrev(_view, state) {
    removeActiveStateAtIndex(state.index);
    state.index--;
    state.index = state.index === -1 ? state.suggestions.length - 1 : state.index;
    addActiveStateAtIndex(state.index);
    scrollActiveItemIntoView(state.index);
  };
  const debouncedSearch = debounce(opts.delay, (state, view) => {
    opts.getSuggestions(state.text, suggestions => {
      const slicedSuggestions = suggestions.slice(0, opts.maxNoOfSuggestions);
      state.suggestions = slicedSuggestions;
      showList(view, state, slicedSuggestions);
    });
  });

  /**
   * See https://prosemirror.net/docs/ref/#state.Plugin_System
   * for the plugin properties spec.
   */
  const pluginKey = new PluginKey(`${opts.type}_${uuid()}_autosuggestions`);
  const useAtMentionNodeViews = Boolean(pluginOptions.AtMentionComponent || pluginOptions.onAtMentionClick);
  return new Plugin({
    /* FIXME: The `Plugin` class type does doesn't forward a type argument for this property;
       it should be `PluginKey<MentionsPluginState>`, instead it is `PluginKey<any>`. */
    key: pluginKey,
    // we will need state to track if suggestion dropdown is currently active or not
    state: {
      init() {
        return getNewState();
      },
      apply(tr, __state) {
        // compute state.active for current transaction and return
        const meta = tr.getMeta(pluginKey);
        if (meta) {
          return meta;
        }
        const newState = getNewState();
        const selection = tr.selection;
        if (selection.from !== selection.to) {
          return newState;
        }
        const $position = selection.$from;
        const match = getMatch($position, opts);

        // if match found update state
        if (match) {
          newState.active = true;
          newState.range = match.range;
          newState.text = match.queryText;
        }
        return newState;
      }
    },
    props: Object.assign({}, useAtMentionNodeViews && {
      nodeViews: {
        atmention: (node, view, getPos) => {
          return new AtMentionNodeView(node, view, getPos, {
            component: pluginOptions.AtMentionComponent || DefaultAtMentionComponent,
            onAtMentionClick: pluginOptions.onAtMentionClick
          });
        }
      }
    }, {
      // We'll need props to hi-jack keydown/keyup & enter events when suggestion dropdown
      // is active.
      handleKeyDown(view, e) {
        const state = pluginKey.getState(view.state);

        // don't handle if no suggestions or not in active mode
        if (!state || !state.active && !state.suggestions.length) {
          return false;
        }

        // if any of the below keys, override with custom handlers.
        const enter = e.key === 'Enter';
        const down = e.key === 'ArrowDown';
        const up = e.key === 'ArrowUp';
        const esc = e.key === 'Escape';
        const tab = e.key === 'Tab';
        if (down) {
          goNext(view, state);
          return true;
        } else if (up) {
          goPrev(view, state);
          return true;
        } else if (enter || tab) {
          select(view, state);
          return true;
        } else if (esc) {
          hideList();
          const tr = view.state.tr;
          tr.setMeta(pluginKey, getNewState());
          return true;
        } else {
          // didn't handle. handover to prosemirror for handling.
          return false;
        }
      },
      // to decorate the currently active @mention text in ui
      decorations(editorState) {
        const state = pluginKey.getState(editorState);
        if (!state) {
          return null;
        }
        const {
          active,
          range
        } = state;
        if (!active) return null;
        return DecorationSet.create(editorState.doc, [Decoration.inline(range.from, range.to, {
          nodeName: 'span',
          'data-suggestion-text': 'true'
        })]);
      },
      handleDOMEvents: {
        // Reset dismissed on focus so popover shows when clicking back into
        // an editor at a trigger position (e.g., cursor after ":smi")
        focus: () => {
          dismissed = false;
        },
        // hide the suggestion list when the editor is blurred
        blur: (view, event) => {
          try {
            const state = pluginKey.getState(view.state);
            const focusEvent = event;
            const focusedElement = focusEvent.relatedTarget;
            const didNotClickSuggestion = !focusedElement || !el.contains(focusedElement);
            if (state && state.active && didNotClickSuggestion) {
              hideList();
            }
          } catch (error) {
            Raven.captureException(error, {
              extra: {
                message: 'ProseMirror Mentions Plugin - Error handling blur event'
              }
            });
          }
        }
      }
    }),
    // To track down state mutations and add dropdown reactions
    view() {
      return {
        update: view => {
          const state = this.key && this.key.getState(view.state);
          if (state) {
            const hasNoSearchText = !state.text && opts.canSearch;
            const shouldNotSearch = !state.active && !opts.canSearch;
            if (hasNoSearchText || shouldNotSearch) {
              hideList();
              // Reset tracking when popover is hidden due to no search text
              // This ensures any new trigger (even at same position) will reset dismissed flag
              lastRange = {
                from: 0,
                to: 0
              };
              lastText = '';
            } else {
              // Reset dismissed when user types new text or starts trigger at new position
              const rangeChanged = state.range.from !== lastRange.from;
              const textChanged = state.text !== lastText;
              if (rangeChanged || textChanged) {
                dismissed = false;
                lastRange = state.range;
                lastText = state.text;
              }
              debouncedSearch(state, view);
            }
          }
        },
        destroy: () => {
          el.remove();
        }
      };
    }
  });
}